import { FC, useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { useDispatch, useSelector } from 'react-redux'
import { selectAuthState } from 'app/store/selectors'
import { RouteUrl } from '.'
import {
    setAwsCredentials,
    setIsLoading,
    setMsalCredentials,
    setTuhiUser,
} from 'app/store/slices/authSlice'
import { restoreLoginSessionOnReload } from 'app/api/auth/repository/authenticationRepository'
import { logEvent } from 'app/logging/firebase'
import { requireTuhiUser } from 'app/models/domain/user/tuhiUser'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`

export const RootRoute: FC = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const { hash, pathname } = useLocation()
    const { isAuthenticated, isAuthorized } = useSelector(selectAuthState)
    const [isSigninRoute, setIsSigninRoute] = useState(false)
    const [isAuthenticatedRoute, setIsAuthenticatedRoute] = useState(false)

    useEffect(() => {
        if (hash) {
            const element = document.getElementById(hash.replace('#', ''))

            if (element) {
                element.scrollIntoView({ behavior: 'smooth' })
            }
        }
    }, [hash])

    useEffect(() => {
        setIsSigninRoute(
            RouteUrl.Authenticated.match(pathname) ||
                RouteUrl.LogIn.match(pathname)
        )
        setIsAuthenticatedRoute(
            RouteUrl.getAuthenticatedRoutes().some((route) =>
                route.match(pathname)
            )
        )
    }, [pathname])

    useEffect(() => {
        const handleRouteBaseOnAuthState = async (isSignin: boolean) => {
            if (isAuthenticated && isAuthorized && isSignin) {
                logEvent('INFO', 'login_success', 'User authenticated')
                navigate(RouteUrl.NewConsult.path)
            } else if (isAuthenticated && !isAuthorized) {
                navigate(RouteUrl.Unauthorized.path)
            } else if (!isAuthenticated && isAuthenticatedRoute) {
                await restoreLoginSessionOnReload(
                    (authResponse) => {
                        dispatch(
                            setMsalCredentials(authResponse.msalCredentials)
                        )
                        dispatch(
                            setTuhiUser(requireTuhiUser(authResponse).toData())
                        )
                        dispatch(setAwsCredentials(authResponse.awsCredentials))
                    },
                    () => {
                        navigate(RouteUrl.LogIn.path)
                        dispatch(setIsLoading(false))
                    },
                    () => {
                        dispatch(setIsLoading(true))
                    },
                    () => {
                        dispatch(setIsLoading(false))
                    }
                )
            }
        }

        handleRouteBaseOnAuthState(isSigninRoute)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- navigate is not identity-stable in react-router 6 without a data router: re-running auth routing on every navigation could restore the session or redirect again
    }, [isAuthorized, isAuthenticated, isSigninRoute, isAuthenticatedRoute])

    return (
        <Container>
            <Outlet />
        </Container>
    )
}
